
import Component from "vue-class-component";
import Vue from "vue";
import {Location} from "vue-router";
import {Prop} from "vue-property-decorator";
import {ApplicationViewModel, RequirementFulfillmentStatus, RequirementGroupModel} from "@/api-client";
import NavApplicationMenuItem from "@/features/applications/components/NavApplicationMenuItem.vue";
import NavApplicationMenuItemGroup from "@/features/applications/components/NavApplicationMenuItemGroup.vue";

interface IMenuItem {
    readonly key: string;
    readonly componentName: string;
}

export class MenuItem implements IMenuItem {
    constructor(
        public readonly name: string,
        public readonly status: RequirementFulfillmentStatus | null,
        public readonly location: Location) {
    }

    get key(): string {
        return JSON.stringify(this.location);
    }

    get componentName(): string {
        return "NavApplicationMenuItem";
    }
}

export class MenuItemGroup implements IMenuItem {
    constructor(
        public readonly id: string,
        public readonly name: string,
        public readonly items: MenuItem[]) {
    }

    get key(): string {
        return this.id;
    }

    get componentName(): string {
        return "NavApplicationMenuItemGroup";
    }
}

@Component({
    components: {
        NavApplicationMenuItem,
        NavApplicationMenuItemGroup
    }
})
export default class NavApplication extends Vue {
    @Prop({required: true})
    application!: ApplicationViewModel;

    getIcon(status: RequirementFulfillmentStatus): string {
        switch (status) {
            case RequirementFulfillmentStatus.Unfulfilled:
                return "far fa-dot-circle color-gray-300";
            case RequirementFulfillmentStatus.Fulfilled:
                return "fas fa-dot-circle color-primary";
            case RequirementFulfillmentStatus.FulfilledAutomatically:
                return "fas fa-dot-circle color-primary";
            case RequirementFulfillmentStatus.Accepted:
                return "fas fa-check-circle color-green";
            case RequirementFulfillmentStatus.Rejected:
                return "fas fa-exclamation-triangle color-red";
            case RequirementFulfillmentStatus.Deleted:
                return "fas fa-trash color-red";
        }
        return "";
    }

    get menuItems(): IMenuItem[] {
        const createRequirementLocation = (requirementId: string): Location => {
            return {
                name: "RequirementOverview",
                params: {requirementId: requirementId}
            }
        };

        const ensureMenuItemGroup = (menuItems: IMenuItem[], requirementGroup: RequirementGroupModel): MenuItemGroup => {
            const existingGroup = menuItems.find(x => x instanceof MenuItemGroup && x.id == requirementGroup.id);
            if (existingGroup)
                return existingGroup as MenuItemGroup;

            const newGroup = new MenuItemGroup(requirementGroup.id, requirementGroup.name, []);
            menuItems.push(newGroup);
            return newGroup;
        };

        const menuItemList: IMenuItem[] = [];

        this.application.requirementFulfillments
            .forEach(requirementFulfillment => {
                const menuItem = new MenuItem(
                    requirementFulfillment.requirement.name,
                    requirementFulfillment.status,
                    createRequirementLocation(requirementFulfillment.requirementId));

                if (!requirementFulfillment.requirement.groupId) {
                    menuItemList.push(menuItem);
                } else {
                    const requirementGroup = this.application.requirementGroups.find(x => x.id == requirementFulfillment.requirement.groupId)!;
                    const menuItemGroup = ensureMenuItemGroup(menuItemList, requirementGroup);
                    menuItemGroup.items.push(menuItem);
                }
            });

        return menuItemList;
    }
}
